import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarGroupAction,
  useSidebar
} from '@components/ui/sidebar'
import { NavLink, useLocation, Link } from 'react-router-dom'
import { PlusIcon } from 'lucide-react'
import React from 'react'

// Item component for a single navigation item
const Item = ({ item }) => {
  const location = useLocation()
  const { isMobile, setOpenMobile } = useSidebar()
  
  const isPathActive = (path) => {
    if (item.exact) {
      return location.pathname === path
    }
    return location.pathname.startsWith(path)
  }
  
  // Function to close sidebar on mobile
  const handleClick = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
    if (item.onClick) {
      item.onClick()
    }
  }

  // Regular menu item without sub-items
  if (!item.items) {
    return (
      <SidebarMenuItem className='ml-2 mr-2' key={item.name}>
        <If condition={item.onClick}>
          <SidebarMenuButton onClick={handleClick} isActive={isPathActive(item.href)}>
            <If condition={item.icon}>
              <item.icon />
            </If>
            <span className='text-base'>{item.name}</span>
          </SidebarMenuButton>
        </If>
        <If condition={!item.onClick}>
          <SidebarMenuButton asChild isActive={isPathActive(item.href)}>
            <NavLink to={item.href} end={item.end} onClick={handleClick}>
              <If condition={item.icon}>
                <item.icon />
              </If>
              <span className='text-base'>{item.name}</span>
            </NavLink>
          </SidebarMenuButton>
        </If>
      </SidebarMenuItem>
    )
  }

  // Group with sub-items
  return (
    <SidebarGroup key={item.name}>
      <SidebarGroupLabel asChild>
        <NavLink to={item.href} end={item.end} onClick={handleClick} className='flex items-center'>
          <If condition={item.icon}>
            <item.icon />
          </If>
          <span className='text-sm font-semibold'>{item.name}</span>
        </NavLink>
      </SidebarGroupLabel>
      <If condition={item.create}>
        <SidebarGroupAction title={`Add to ${item.name}`} onClick={item.create}>
          <PlusIcon size={16} className='stroke-2' />
          <span className='sr-only'>{`Add to ${item.name}`}</span>
        </SidebarGroupAction>
      </If>
      <SidebarGroupContent>
        <SidebarMenu>
          {item.items.map((subItem) => (
            <SidebarMenuItem key={subItem.name}>
              <SidebarMenuButton asChild isActive={subItem.exact ? location.pathname === subItem.href : location.pathname.startsWith(subItem.href)}>
                <NavLink to={subItem.href} end={subItem.end} onClick={handleClick}>
                  <If condition={subItem.icon}>
                    <subItem.icon size={16} />
                  </If>
                  <span className='text-base text-light'>{subItem.name}</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}

// List component for rendering a collection of navigation items
const List = ({ items }) => {
  return (
    <SidebarMenu>
      {items.map((item) => (
        <Item key={item.name} item={item} />
      ))}
    </SidebarMenu>
  )
}

// NavbarContent component for wrapping content section
const NavbarContent = ({ children }) => {
  return (
    <SidebarContent>
      {children}
    </SidebarContent>
  )
}

// NavbarHeader component for wrapping header section
const NavbarHeader = ({ children }) => {
  return (
    <SidebarHeader>
      {children}
    </SidebarHeader>
  )
}

// NavbarFooter component for wrapping footer section
const NavbarFooter = ({ children }) => {
  return (
    <SidebarFooter>
      {children}
    </SidebarFooter>
  )
}

// Default logo component for convenience
const NavbarLogo = () => (
  <div className='flex justify-start p-2'>
    <Link to='/'>
      <img
        className='h-12 w-auto hover:cursor-pointer dark:hidden'
        src='/mindjoy-logo.svg'
        alt='Mindjoy logo'
      />
      <img
        className='h-12 w-auto hover:cursor-pointer hidden dark:block'
        src='/logo.svg'
        alt='Mindjoy logo'
      />
    </Link>
  </div>
)

// ShadcnNav component for displaying navigation items
const Navbar = ({
  children
}) => {
  // Find the relevant children by type
  const findChildByType = (type) => {
    return React.Children.toArray(children).find(
      child => React.isValidElement(child) && child.type === type
    )
  }

  const headerElement = findChildByType(NavbarHeader)
  const contentElement = findChildByType(NavbarContent)
  const footerElement = findChildByType(NavbarFooter)

  return (
    <Sidebar>
      {headerElement}
      {contentElement}
      {footerElement}
    </Sidebar>
  )
}

// Attach components to Navbar
Navbar.List = List
Navbar.Item = Item
Navbar.Content = NavbarContent
Navbar.Header = NavbarHeader
Navbar.Footer = NavbarFooter
Navbar.Logo = NavbarLogo

export default Navbar
